import {
  Alert,
  Box,
  Button,
  Group,
  Modal,
  ScrollArea,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle, IconFileCheck, IconTrash } from "@tabler/icons-react";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  useDeleteAmendmentDraft,
  useSaveAmendmentDraft,
  useSignOutAmendment,
} from "../../hooks/useAmendment";
import {
  AmendmentServiceError,
  type AmendmentDraft,
} from "../../services/amendment.service";
import type { ReportPreviewEvidence } from "../../services/signOff.service";
import { findUnresolvedAddendumVariables } from "../../utils/addendumVariables";
import {
  scrollToFirstError,
  showValidationNotification,
} from "../../utils/formValidation";
import { isEmptyHtml } from "../../utils/helpers";
import { SignOffAuthModal } from "../workflows/SignOffAuthModal";
import { AmendmentReportPreviewModal } from "./AmendmentReportPreviewModal";
import {
  buildAmendmentDraftInput,
  buildInitialAmendmentValues,
  type AmendmentEditorFormModalProps,
  type AmendmentFormValues,
} from "./amendmentEditorForm";
import { ConfirmAddendumActionModal } from "./ConfirmAddendumActionModal";
import RichTextDiagnosis from "./RichTextDiagnosis";

export function AmendmentEditorFormModal({
  opened,
  caseId,
  caseNumber,
  sessionId,
  initialDraft,
  options,
  onClose,
  onSigned,
  onSessionUpdated,
  reviewRequired,
}: AmendmentEditorFormModalProps) {
  const saveDraft = useSaveAmendmentDraft(caseId);
  const deleteDraft = useDeleteAmendmentDraft(caseId);
  const signOut = useSignOutAmendment(caseId);
  const [currentDraft, setCurrentDraft] = useState(initialDraft);
  const [authenticationOpened, setAuthenticationOpened] = useState(false);
  const [authenticationError, setAuthenticationError] = useState<string | null>(
    null
  );
  const [draftPendingSignature, setDraftPendingSignature] =
    useState<AmendmentDraft | null>(null);
  const [evidencePendingSignature, setEvidencePendingSignature] = useState<
    ReportPreviewEvidence | undefined
  >();
  const [previewDraft, setPreviewDraft] = useState<AmendmentDraft | null>(null);
  const [previewOpened, setPreviewOpened] = useState(false);
  const [deleteConfirmationOpened, setDeleteConfirmationOpened] =
    useState(false);
  const [discardConfirmationOpened, setDiscardConfirmationOpened] =
    useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const amendmentEditorRef = useRef<HTMLDivElement>(null);
  const form = useForm<AmendmentFormValues>({
    initialValues: buildInitialAmendmentValues(initialDraft),
    validateInputOnBlur: true,
    validate: {
      contentHtml: (value) =>
        isEmptyHtml(value) ? "Enter the amended report text" : null,
    },
  });

  const macroOptions = useMemo(
    () =>
      options.macros.map((macro) => ({
        value: macro.id,
        label: macro.shortcut
          ? `${macro.name} (${macro.shortcut})`
          : macro.name,
      })),
    [options.macros]
  );
  const isBusy = useMemo(
    () => saveDraft.isPending || deleteDraft.isPending || signOut.isPending,
    [deleteDraft.isPending, saveDraft.isPending, signOut.isPending]
  );

  const handleContentChange = useCallback(
    (contentHtml: string) => {
      form.setFieldValue("contentHtml", contentHtml);
      form.clearFieldError("contentHtml");
    },
    [form]
  );

  const focusFirstAmendmentVariable = useCallback(() => {
    setTimeout(() => {
      const editorRoot = amendmentEditorRef.current;
      const firstVariable = editorRoot?.querySelector<HTMLElement>(
        "[data-variable-input]"
      );
      if (firstVariable) {
        firstVariable.focus();
        return;
      }
      editorRoot?.querySelector<HTMLElement>(".ProseMirror")?.focus();
    }, 150);
  }, []);

  const handleMacroChange = useCallback(
    (macroId: string | null) => {
      form.setFieldValue("sourceMacroId", macroId);
      const macro = options.macros.find(
        (candidate) => candidate.id === macroId
      );
      if (!macro) return;
      form.setFieldValue("contentHtml", macro.contentHtml);
      form.clearFieldError("contentHtml");
      focusFirstAmendmentVariable();
    },
    [focusFirstAmendmentVariable, form, options.macros]
  );

  const persistDraft = useCallback(async (): Promise<AmendmentDraft | null> => {
    const validation = form.validate();
    if (validation.hasErrors) {
      showValidationNotification("Complete the required amendment fields.");
      scrollToFirstError(form);
      return null;
    }
    const saved = await saveDraft.mutateAsync({
      draftId: currentDraft?.id,
      input: {
        ...buildAmendmentDraftInput(form.values, currentDraft?.revision),
        diagnosticContext: Boolean(currentDraft?.diagnosticData),
      },
    });
    setCurrentDraft(saved);
    form.resetDirty();
    return saved;
  }, [currentDraft, form, saveDraft]);

  const completeSignature = useCallback(
    async (
      draft: AmendmentDraft,
      password?: string,
      previewEvidence?: ReportPreviewEvidence
    ) => {
      try {
        const result = await signOut.mutateAsync({
          draftId: draft.id,
          revision: draft.revision,
          sessionId,
          password,
          previewEvidence,
        });
        setAuthenticationOpened(false);
        setAuthenticationError(null);
        const deliveryMessage = result.deliveryQueueFailed
          ? "Automatic delivery could not be queued. Retry it from report delivery."
          : result.deliveryQueue.length > 0
          ? `Delivery was queued to ${result.deliveryQueue.length} destination(s).`
          : "No automatic delivery destination was queued.";
        const message = result.pdfGenerationQueueFailed
          ? `${deliveryMessage} PDF generation also needs a retry.`
          : deliveryMessage;
        notifications.show({
          title: "Amendment signed out",
          message: `Report version ${result.reportVersion} was created. ${message}`,
          color:
            result.deliveryQueueFailed || result.pdfGenerationQueueFailed
              ? "yellow"
              : "green",
        });
        onSessionUpdated?.(result.sessionId, result.sessionExpiresAt);
        onSigned();
        onClose();
      } catch (error) {
        if (
          error instanceof AmendmentServiceError &&
          error.kind === "authentication_required"
        ) {
          setDraftPendingSignature(draft);
          setEvidencePendingSignature(previewEvidence);
          setAuthenticationError(
            password ? "The password was not accepted. Try again." : null
          );
          setAuthenticationOpened(true);
          return;
        }
        if (
          error instanceof AmendmentServiceError &&
          (error.kind === "review_required" || error.kind === "review_stale")
        ) {
          setAuthenticationOpened(false);
          if (error.sessionId) {
            onSessionUpdated?.(error.sessionId, error.sessionExpiresAt);
          }
          setPreviewDraft(draft);
          setPreviewOpened(true);
          setFormError(error.message);
          return;
        }
        setFormError(
          error instanceof AmendmentServiceError
            ? error.message
            : "The amendment could not be signed."
        );
      }
    },
    [onClose, onSessionUpdated, onSigned, sessionId, signOut]
  );

  const handleSave = useCallback(async () => {
    setFormError(null);
    try {
      const saved = await persistDraft();
      if (!saved) return;
      notifications.show({
        title: "Draft saved",
        message: "No report version or delivery was created.",
        color: "green",
      });
    } catch (error) {
      setFormError(
        error instanceof AmendmentServiceError
          ? error.message
          : "The amendment draft could not be saved."
      );
    }
  }, [persistDraft]);

  const handleContinue = useCallback(async () => {
    setFormError(null);
    const unresolved = findUnresolvedAddendumVariables(form.values.contentHtml);
    if (unresolved.length > 0) {
      setFormError(
        `Complete these variables before signing: ${unresolved.join(", ")}`
      );
      return;
    }
    try {
      const saved = await persistDraft();
      if (!saved) return;
      if (reviewRequired || saved.diagnosticData) {
        setPreviewDraft(saved);
        setPreviewOpened(true);
        return;
      }
      await completeSignature(saved);
    } catch (error) {
      setFormError(
        error instanceof AmendmentServiceError
          ? error.message
          : "The amendment could not be prepared for signing."
      );
    }
  }, [
    completeSignature,
    form.values.contentHtml,
    persistDraft,
    reviewRequired,
  ]);

  const handleReviewedSignOut = useCallback(
    async (previewEvidence?: ReportPreviewEvidence) => {
      if (!previewDraft) return;
      setPreviewOpened(false);
      await completeSignature(previewDraft, undefined, previewEvidence);
    },
    [completeSignature, previewDraft]
  );
  const handleAuthenticate = useCallback(
    async (password: string) => {
      if (!draftPendingSignature) return;
      await completeSignature(
        draftPendingSignature,
        password,
        evidencePendingSignature
      );
    },
    [completeSignature, draftPendingSignature, evidencePendingSignature]
  );
  const handleRequestClose = useCallback(() => {
    if (form.isDirty()) {
      setDiscardConfirmationOpened(true);
      return;
    }
    onClose();
  }, [form, onClose]);
  const handleDiscard = useCallback(() => {
    setDiscardConfirmationOpened(false);
    onClose();
  }, [onClose]);
  const handleDelete = useCallback(async () => {
    if (!currentDraft) return;
    try {
      await deleteDraft.mutateAsync(currentDraft.id);
      setCurrentDraft(null);
      setDeleteConfirmationOpened(false);
      notifications.show({
        title: "Draft deleted",
        message: "The unsigned amendment draft was removed.",
        color: "green",
      });
      onClose();
    } catch (error) {
      setFormError(
        error instanceof AmendmentServiceError
          ? error.message
          : "The amendment draft could not be deleted."
      );
    }
  }, [currentDraft, deleteDraft, onClose]);
  const handleOpenDeleteConfirmation = useCallback(() => {
    setDeleteConfirmationOpened(true);
  }, []);
  const handleCloseDeleteConfirmation = useCallback(() => {
    setDeleteConfirmationOpened(false);
  }, []);
  const handleCloseDiscardConfirmation = useCallback(() => {
    setDiscardConfirmationOpened(false);
  }, []);
  const handleCloseAuthentication = useCallback(() => {
    setAuthenticationOpened(false);
  }, []);
  const handleClosePreview = useCallback(() => {
    setPreviewOpened(false);
  }, []);

  return (
    <>
      <Modal
        opened={opened && !previewOpened}
        onClose={handleRequestClose}
        title={`Add Amendment — ${caseNumber}`}
        size="90vw"
        centered
        closeOnClickOutside={false}
        closeOnEscape={false}
        closeButtonProps={{ "aria-label": "Close amendment editor" }}
        styles={{
          content: {
            display: "flex",
            flexDirection: "column",
            height: "92vh",
          },
          body: {
            display: "flex",
            flex: 1,
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
            padding: 0,
          },
        }}
      >
        <ScrollArea style={{ flex: 1, minHeight: 0 }} px="lg" py="md">
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Save freely as a draft. The signed report changes only after
              review and authentication.
            </Text>
            {formError && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
                title="Amendment needs attention"
                role="alert"
              >
                {formError}
              </Alert>
            )}
            <Select
              label="Amendment macro"
              placeholder="Optional — choose a saved macro"
              data={macroOptions}
              value={form.values.sourceMacroId}
              onChange={handleMacroChange}
              searchable
              clearable
              nothingFoundMessage="No active amendment macros"
            />
            <Box ref={amendmentEditorRef}>
              <RichTextDiagnosis
                label="Amendment explanation"
                withAsterisk
                value={form.values.contentHtml}
                onChange={handleContentChange}
                placeholder="Explain what changed and why."
                minHeight={420}
                error={form.errors.contentHtml as string | undefined}
                supplementalTypography="schema"
              />
            </Box>
            <Text size="xs" c="dimmed">
              After selecting a macro, use Tab and Shift+Tab to move between its
              fillable fields.
            </Text>
          </Stack>
        </ScrollArea>
        <Group
          justify="space-between"
          px="lg"
          py="md"
          style={{
            borderTop: "1px solid var(--mantine-color-gray-3)",
            flexShrink: 0,
          }}
        >
          <Group>
            {currentDraft && (
              <Button
                variant="subtle"
                color="red"
                leftSection={<IconTrash size={16} />}
                onClick={handleOpenDeleteConfirmation}
              >
                Delete Draft
              </Button>
            )}
          </Group>
          <Group>
            <Button
              variant="light"
              loading={saveDraft.isPending}
              onClick={handleSave}
            >
              Save Draft
            </Button>
            <Button
              leftSection={<IconFileCheck size={16} />}
              loading={isBusy}
              onClick={handleContinue}
            >
              {reviewRequired ? "Continue to Review" : "Sign Out Amendment"}
            </Button>
          </Group>
        </Group>
      </Modal>
      <ConfirmAddendumActionModal
        opened={deleteConfirmationOpened}
        title="Delete unsigned amendment draft?"
        message="This removes only the unsigned draft. It does not change any signed report."
        confirmLabel="Delete Draft"
        loading={deleteDraft.isPending}
        onCancel={handleCloseDeleteConfirmation}
        onConfirm={handleDelete}
      />
      <ConfirmAddendumActionModal
        opened={discardConfirmationOpened}
        title="Discard unsaved changes?"
        message="Changes made since the last draft save will be lost."
        confirmLabel="Discard Changes"
        onCancel={handleCloseDiscardConfirmation}
        onConfirm={handleDiscard}
      />
      <SignOffAuthModal
        opened={authenticationOpened}
        onClose={handleCloseAuthentication}
        onAuthenticate={handleAuthenticate}
        isLoading={signOut.isPending}
        error={authenticationError}
      />
      {previewDraft && (
        <AmendmentReportPreviewModal
          opened={opened && previewOpened}
          caseId={caseId}
          draft={previewDraft}
          onClose={handleClosePreview}
          onSignOut={handleReviewedSignOut}
          signOutPending={signOut.isPending}
        />
      )}
    </>
  );
}
